// This file is automatically generated by webgen from the struct descriptions in /generated-json/structs.
// Files in /generated-json are created from the structs configured in the 'main' function of /cmd/struct2json/main.go.
//
// DO NOT EDIT THIS FILE except where designated below.

/* eslint-disable no-relative-import-paths/no-relative-import-paths */

import type { DateTime } from 'luxon';
import type { PartialDeep } from 'type-fest';

import * as DateHelpers from '../../../utils/dateHelpers';
import type { ExcludeMethodsDeep } from '../../../utils/types';
import { NIL_UUID } from '../../../utils/uuid';
import Enums from '../Enums';
import {
  NewQuoteSuspendedOrder,
  type QuoteSuspendedOrder,
  type QuoteSuspendedOrderWireFormat,
} from '../QuoteSuspendedOrder/QuoteSuspendedOrder';

export class QuoteSummary {
  readonly id: string;
  readonly quoteNumber: string;
  readonly projectJobNumber: string | null;
  readonly purchaseOrderNumber: string | null;
  readonly name: string | null;
  readonly revisionNumber: number;
  readonly isLatestRevision: boolean;
  readonly projectName: string;
  readonly statusSystemBehavior: Enums.QuoteStatusSystemBehavior;
  readonly companyName: string | null;
  readonly customerCode: string | null;
  readonly expirationDate: string | null;
  readonly bidDate: string | null;
  readonly statusId: string;
  readonly statusName: string;
  readonly isDraft: boolean;
  readonly isApprovalRequest: boolean;
  readonly isRejected: boolean;
  readonly isApprovedToSend: boolean;
  readonly isSent: boolean;
  readonly isArchived: boolean;
  readonly salesRepName: string;
  readonly revenue: string;
  readonly currencyCode: Enums.CurrencyCode;
  readonly address: string;
  readonly estimatedCuyd: string;
  readonly plantName: string;
  readonly plantId: string;
  readonly plants: string;
  readonly marketId: string;
  readonly divisionId: string | null;
  readonly projectId: string;
  readonly companyId: string | null;
  readonly creationDate: string | null;
  readonly revisionDate: string | null;
  readonly externalID: string | null;
  readonly firstSentAt: string | null;
  readonly lastSentAt: string | null;
  readonly primaryContactID: string | null;
  readonly readyMixQuantity: string;
  readonly readyMixQuantityUnit: Enums.Unit;
  readonly readyMixRevenueAmount: string;
  readonly asphaltQuantity: string;
  readonly asphaltQuantityUnit: Enums.Unit;
  readonly asphaltRevenueAmount: string;
  readonly aggregateQuantity: string;
  readonly aggregateQuantityUnit: Enums.Unit;
  readonly aggregateRevenueAmount: string;
  readonly lastStatusChangeAt: DateTime | null;
  readonly lastStatusChangeBy: string | null;
  readonly productCategory: Enums.ProductCategory | null;
  readonly plantIsArchived: boolean;
  readonly canReview: boolean;
  readonly isSuspended: boolean;
  readonly suspendedOrders: QuoteSuspendedOrder[];

  constructor(data: { [key: string]: any } = {}) {
    this.id = data.id === undefined ? NIL_UUID : data.id;
    this.quoteNumber = data.quoteNumber === undefined ? '' : data.quoteNumber;
    this.projectJobNumber = data.projectJobNumber === undefined ? null : data.projectJobNumber;
    this.purchaseOrderNumber =
      data.purchaseOrderNumber === undefined ? null : data.purchaseOrderNumber;
    this.name = data.name === undefined ? null : data.name;
    this.revisionNumber = data.revisionNumber === undefined ? 0 : data.revisionNumber;
    this.isLatestRevision = data.isLatestRevision === undefined ? false : data.isLatestRevision;
    this.projectName = data.projectName === undefined ? '' : data.projectName;
    this.statusSystemBehavior =
      data.statusSystemBehavior === undefined
        ? Enums.QuoteStatusSystemBehavior.Draft
        : data.statusSystemBehavior;
    this.companyName = data.companyName === undefined ? null : data.companyName;
    this.customerCode = data.customerCode === undefined ? null : data.customerCode;
    this.expirationDate = data.expirationDate === undefined ? null : data.expirationDate;
    this.bidDate = data.bidDate === undefined ? null : data.bidDate;
    this.statusId = data.statusId === undefined ? NIL_UUID : data.statusId;
    this.statusName = data.statusName === undefined ? '' : data.statusName;
    this.isDraft = data.isDraft === undefined ? false : data.isDraft;
    this.isApprovalRequest = data.isApprovalRequest === undefined ? false : data.isApprovalRequest;
    this.isRejected = data.isRejected === undefined ? false : data.isRejected;
    this.isApprovedToSend = data.isApprovedToSend === undefined ? false : data.isApprovedToSend;
    this.isSent = data.isSent === undefined ? false : data.isSent;
    this.isArchived = data.isArchived === undefined ? false : data.isArchived;
    this.salesRepName = data.salesRepName === undefined ? '' : data.salesRepName;
    this.revenue = data.revenue === undefined ? '' : data.revenue;
    this.currencyCode =
      data.currencyCode === undefined ? Enums.CurrencyCode.USD : data.currencyCode;
    this.address = data.address === undefined ? '' : data.address;
    this.estimatedCuyd = data.estimatedCuyd === undefined ? '0' : data.estimatedCuyd;
    this.plantName = data.plantName === undefined ? '' : data.plantName;
    this.plantId = data.plantId === undefined ? NIL_UUID : data.plantId;
    this.plants = data.plants === undefined ? '' : data.plants;
    this.marketId = data.marketId === undefined ? NIL_UUID : data.marketId;
    this.divisionId = data.divisionId === undefined ? null : data.divisionId;
    this.projectId = data.projectId === undefined ? NIL_UUID : data.projectId;
    this.companyId = data.companyId === undefined ? null : data.companyId;
    this.creationDate = data.creationDate === undefined ? null : data.creationDate;
    this.revisionDate = data.revisionDate === undefined ? null : data.revisionDate;
    this.externalID = data.externalID === undefined ? null : data.externalID;
    this.firstSentAt = data.firstSentAt === undefined ? null : data.firstSentAt;
    this.lastSentAt = data.lastSentAt === undefined ? null : data.lastSentAt;
    this.primaryContactID = data.primaryContactID === undefined ? null : data.primaryContactID;
    this.readyMixQuantity = data.readyMixQuantity === undefined ? '0' : data.readyMixQuantity;
    this.readyMixQuantityUnit =
      data.readyMixQuantityUnit === undefined ? Enums.Unit.CuYd : data.readyMixQuantityUnit;
    this.readyMixRevenueAmount =
      data.readyMixRevenueAmount === undefined ? '0' : data.readyMixRevenueAmount;
    this.asphaltQuantity = data.asphaltQuantity === undefined ? '0' : data.asphaltQuantity;
    this.asphaltQuantityUnit =
      data.asphaltQuantityUnit === undefined ? Enums.Unit.Tn : data.asphaltQuantityUnit;
    this.asphaltRevenueAmount =
      data.asphaltRevenueAmount === undefined ? '0' : data.asphaltRevenueAmount;
    this.aggregateQuantity = data.aggregateQuantity === undefined ? '0' : data.aggregateQuantity;
    this.aggregateQuantityUnit =
      data.aggregateQuantityUnit === undefined ? Enums.Unit.Tn : data.aggregateQuantityUnit;
    this.aggregateRevenueAmount =
      data.aggregateRevenueAmount === undefined ? '0' : data.aggregateRevenueAmount;
    this.lastStatusChangeAt = DateHelpers.ParseServerNullableDate(data.lastStatusChangeAt);
    this.lastStatusChangeBy =
      data.lastStatusChangeBy === undefined ? null : data.lastStatusChangeBy;
    this.productCategory = data.productCategory === undefined ? null : data.productCategory;
    this.plantIsArchived = data.plantIsArchived === undefined ? false : data.plantIsArchived;
    this.canReview = data.canReview === undefined ? false : data.canReview;
    this.isSuspended = data.isSuspended === undefined ? false : data.isSuspended;
    this.suspendedOrders = (data.suspendedOrders ?? []).map((suspendedOrders: any) =>
      NewQuoteSuspendedOrder(suspendedOrders),
    );
  }

  static zero(): QuoteSummary {
    const zeroValues: ExcludeMethodsDeep<QuoteSummary> = {
      id: NIL_UUID,
      quoteNumber: '',
      projectJobNumber: null,
      purchaseOrderNumber: null,
      name: null,
      revisionNumber: 0,
      isLatestRevision: false,
      projectName: '',
      statusSystemBehavior: Enums.QuoteStatusSystemBehavior.Draft,
      companyName: null,
      customerCode: null,
      expirationDate: null,
      bidDate: null,
      statusId: NIL_UUID,
      statusName: '',
      isDraft: false,
      isApprovalRequest: false,
      isRejected: false,
      isApprovedToSend: false,
      isSent: false,
      isArchived: false,
      salesRepName: '',
      revenue: '',
      currencyCode: Enums.CurrencyCode.USD,
      address: '',
      estimatedCuyd: '0',
      plantName: '',
      plantId: NIL_UUID,
      plants: '',
      marketId: NIL_UUID,
      divisionId: null,
      projectId: NIL_UUID,
      companyId: null,
      creationDate: null,
      revisionDate: null,
      externalID: null,
      firstSentAt: null,
      lastSentAt: null,
      primaryContactID: null,
      readyMixQuantity: '0',
      readyMixQuantityUnit: Enums.Unit.CuYd,
      readyMixRevenueAmount: '0',
      asphaltQuantity: '0',
      asphaltQuantityUnit: Enums.Unit.Tn,
      asphaltRevenueAmount: '0',
      aggregateQuantity: '0',
      aggregateQuantityUnit: Enums.Unit.Tn,
      aggregateRevenueAmount: '0',
      lastStatusChangeAt: null,
      lastStatusChangeBy: null,
      productCategory: null,
      plantIsArchived: false,
      canReview: false,
      isSuspended: false,
      suspendedOrders: [],
    };
    return new QuoteSummary(zeroValues);
  }

  // ************* DO NOT EDIT ABOVE THIS LINE *************

  // ************* DO NOT EDIT BELOW THIS LINE *************
}

export type QuoteSummaryWireFormat = {
  id: string;
  quoteNumber: string;
  projectJobNumber: string | null;
  purchaseOrderNumber: string | null;
  name: string | null;
  revisionNumber: number;
  isLatestRevision: boolean;
  projectName: string;
  statusSystemBehavior: string;
  companyName: string | null;
  customerCode: string | null;
  expirationDate: string | null;
  bidDate: string | null;
  statusId: string;
  statusName: string;
  isDraft: boolean;
  isApprovalRequest: boolean;
  isRejected: boolean;
  isApprovedToSend: boolean;
  isSent: boolean;
  isArchived: boolean;
  salesRepName: string;
  revenue: string;
  currencyCode: string;
  address: string;
  estimatedCuyd: string;
  plantName: string;
  plantId: string;
  plants: string;
  marketId: string;
  divisionId: string | null;
  projectId: string;
  companyId: string | null;
  creationDate: string | null;
  revisionDate: string | null;
  externalID: string | null;
  firstSentAt: string | null;
  lastSentAt: string | null;
  primaryContactID: string | null;
  readyMixQuantity: string;
  readyMixQuantityUnit: string;
  readyMixRevenueAmount: string;
  asphaltQuantity: string;
  asphaltQuantityUnit: string;
  asphaltRevenueAmount: string;
  aggregateQuantity: string;
  aggregateQuantityUnit: string;
  aggregateRevenueAmount: string;
  lastStatusChangeAt: string | null;
  lastStatusChangeBy: string | null;
  productCategory: string | null;
  plantIsArchived: boolean;
  canReview: boolean;
  isSuspended: boolean;
  suspendedOrders: QuoteSuspendedOrderWireFormat[];
};

type NewQuoteSummaryProps = PartialDeep<QuoteSummary, { recurseIntoArrays: true }>;
export const NewQuoteSummary: (props: NewQuoteSummaryProps) => QuoteSummary = (props) =>
  new QuoteSummary(props);

type NewQuoteSummaryFromWireFormatProps = PartialDeep<
  QuoteSummaryWireFormat,
  { recurseIntoArrays: true }
>;
export const NewQuoteSummaryFromWireFormat: (
  props: NewQuoteSummaryFromWireFormatProps,
) => QuoteSummary = (props) => new QuoteSummary(props);
